import { useSearchParams } from "react-router-dom";

type UrlFiltersOptions = {
  defaultLimit?: number;
};

export default function useUrlFilters({
  defaultLimit = 10,
}: UrlFiltersOptions = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || defaultLimit;
  const search = searchParams.get("search") || "";

  // Generic method to read any future query parameter safely
  const getFilter = (key: string) => searchParams.get(key) || "";

  // Set page
  const setPage = (newPage: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", newPage.toString());
    setSearchParams(nextParams, { replace: true });
  };

  // Set limit (resets page)
  const setLimit = (newLimit: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("limit", newLimit.toString());
    nextParams.set("page", "1");
    setSearchParams(nextParams, { replace: true });
  };

  // Set search text (resets page)
  const setSearch = (newSearch: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (newSearch) {
      nextParams.set("search", newSearch);
    } else {
      nextParams.delete("search");
    }
    nextParams.set("page", "1");
    setSearchParams(nextParams, { replace: true });
  };

  /**
   * Universal setter for any future/arbitrary filters.
   * Resets page to 1 whenever a filter changes.
   */
  const setFilter = (key: string, value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }
    nextParams.set("page", "1");
    setSearchParams(nextParams, { replace: true });
  };

  return {
    page,
    limit,
    search,
    getFilter,
    setPage,
    setLimit,
    setSearch,
    setFilter,
  };
}
